import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { useDebugLogStore } from '../stores/debug-log-store';

export interface AiUsage {
  dailyLimit: number;
  usedToday: number;
  remainingToday: number;
}

export type AiProvider = 'gemini' | 'openrouter';
export type OpenRouterModel =
  | 'gpt-5-nano-2025-08-07'
  | 'openai/gpt-5-mini'
  | 'z-ai/glm-4.7-flash'
  | 'qwen/qwen-2.5-7b-instruct'
  | 'anthropic/claude-3.5-sonnet'
  | 'anthropic/claude-3-haiku'
  | 'google/gemini-2.0-flash-001'
  | 'google/gemini-3-flash-preview'
  | 'openai/gpt-4o-mini'
  | 'openai/gpt-4o';

export interface AiAccessStatus extends AiUsage {
  provider: AiProvider;
  gemini: { hasKey: boolean; maskedKey: string | null };
  openrouter: { hasKey: boolean; maskedKey: string | null; model: OpenRouterModel };
}

export interface GenerateHtmlResponse {
  html: string;
  usage:
    | ({ mode: 'prompts' } & AiUsage)
    | { mode: 'tokens'; inputTokens: number; outputTokens: number; totalTokens: number };
}

type CallableErrorLike = {
  code?: unknown;
  message?: unknown;
  details?: unknown;
};

export function getCallableErrorCode(error: unknown): string | null {
  const candidate = error as CallableErrorLike;
  return typeof candidate?.code === 'string' ? candidate.code : null;
}

export function getCallableErrorMessage(error: unknown): string {
  const candidate = error as CallableErrorLike;
  const code = getCallableErrorCode(error) ?? '';
  const rawMessage =
    (typeof candidate?.message === 'string' && candidate.message) ||
    (error instanceof Error ? error.message : '');
  const lowerRaw = rawMessage.toLowerCase();
  const detailsAsString =
    typeof candidate?.details === 'string'
      ? candidate.details
      : typeof candidate?.details === 'object' && candidate.details !== null
        ? JSON.stringify(candidate.details)
        : '';
  const lowerDetails = detailsAsString.toLowerCase();

  if (
    lowerRaw.includes('failed to fetch') ||
    lowerRaw.includes('cors') ||
    lowerRaw.includes('networkerror') ||
    lowerRaw.includes('net::err_failed')
  ) {
    return 'AI backend недоступен. Задеплойте Firebase Functions и проверьте регион us-central1.';
  }

  if (typeof candidate?.details === 'object' && candidate.details !== null) {
    const detailsObj = candidate.details as { message?: unknown };
    if (typeof detailsObj.message === 'string' && detailsObj.message.trim()) {
      return detailsObj.message;
    }
  }

  if (typeof candidate?.details === 'string' && candidate.details.trim()) {
    return candidate.details;
  }

  if (code === 'functions/resource-exhausted') {
    if (
      lowerRaw.includes('glm') ||
      lowerRaw.includes('openrouter') ||
      lowerRaw.includes('rate limit') ||
      lowerRaw.includes('quota') ||
      lowerDetails.includes('glm') ||
      lowerDetails.includes('openrouter') ||
      lowerDetails.includes('rate limit') ||
      lowerDetails.includes('quota')
    ) {
      return 'Превышен лимит провайдера (GLM/OpenRouter): rate limit или quota. Повторите позже.';
    }
    return 'Дневной лимит исчерпан. Попробуйте завтра или замените ключ.';
  }

  if (code === 'functions/failed-precondition') {
    return 'Предусловие не выполнено. Проверьте ключ в настройках и повторите.';
  }

  if (code === 'functions/internal') {
    return 'Внутренняя ошибка AI-сервера. Повторите запрос; если ошибка повторяется, проверьте логи функции.';
  }

  if (code === 'functions/unavailable') {
    return 'AI сервис временно недоступен. Попробуйте еще раз.';
  }

  if (code === 'functions/unauthenticated') {
    return 'Нужно снова войти в аккаунт.';
  }
  if (typeof candidate?.message === 'string' && candidate.message.trim()) {
    return candidate.message.replace(/^Firebase:\s*/i, '');
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.replace(/^Firebase:\s*/i, '');
  }
  return 'Unexpected error. Please try again.';
}

export async function getAiAccessStatus(): Promise<AiAccessStatus> {
  const callable = httpsCallable<Record<string, never>, AiAccessStatus>(functions, 'getAiAccessStatus');
  const t0 = Date.now();
  try {
    const result = await callable({});
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'getAiAccessStatus:ok',
      data: { ms: Date.now() - t0, provider: result.data.provider },
    });
    return result.data;
  } catch (e) {
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'getAiAccessStatus:error',
      data: { ms: Date.now() - t0, message: getCallableErrorMessage(e), code: getCallableErrorCode(e) },
    });
    throw e;
  }
}

export async function setGeminiApiKey(apiKey: string): Promise<AiAccessStatus> {
  const callable = httpsCallable<{ apiKey: string }, AiAccessStatus>(functions, 'setGeminiApiKey');
  const t0 = Date.now();
  try {
    const result = await callable({ apiKey });
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'setGeminiApiKey:ok',
      data: { ms: Date.now() - t0 },
    });
    return result.data;
  } catch (e) {
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'setGeminiApiKey:error',
      data: { ms: Date.now() - t0, message: getCallableErrorMessage(e), code: getCallableErrorCode(e) },
    });
    throw e;
  }
}

export async function deleteGeminiApiKey(): Promise<AiAccessStatus> {
  const callable = httpsCallable<Record<string, never>, AiAccessStatus>(functions, 'deleteGeminiApiKey');
  const t0 = Date.now();
  try {
    const result = await callable({});
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'deleteGeminiApiKey:ok',
      data: { ms: Date.now() - t0 },
    });
    return result.data;
  } catch (e) {
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'deleteGeminiApiKey:error',
      data: { ms: Date.now() - t0, message: getCallableErrorMessage(e), code: getCallableErrorCode(e) },
    });
    throw e;
  }
}

export async function setOpenRouterApiKey(apiKey: string): Promise<AiAccessStatus> {
  const callable = httpsCallable<{ apiKey: string }, AiAccessStatus>(functions, 'setOpenRouterApiKey');
  const t0 = Date.now();
  try {
    const result = await callable({ apiKey });
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'setOpenRouterApiKey:ok',
      data: { ms: Date.now() - t0 },
    });
    return result.data;
  } catch (e) {
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'setOpenRouterApiKey:error',
      data: { ms: Date.now() - t0, message: getCallableErrorMessage(e), code: getCallableErrorCode(e) },
    });
    throw e;
  }
}

export async function deleteOpenRouterApiKey(): Promise<AiAccessStatus> {
  const callable = httpsCallable<Record<string, never>, AiAccessStatus>(functions, 'deleteOpenRouterApiKey');
  const t0 = Date.now();
  try {
    const result = await callable({});
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'deleteOpenRouterApiKey:ok',
      data: { ms: Date.now() - t0 },
    });
    return result.data;
  } catch (e) {
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'deleteOpenRouterApiKey:error',
      data: { ms: Date.now() - t0, message: getCallableErrorMessage(e), code: getCallableErrorCode(e) },
    });
    throw e;
  }
}

export async function setAiProviderConfig(params: { provider: AiProvider; openrouterModel?: OpenRouterModel }): Promise<AiAccessStatus> {
  const callable = httpsCallable<{ provider: AiProvider; openrouterModel?: OpenRouterModel }, AiAccessStatus>(functions, 'setAiProviderConfig');
  const t0 = Date.now();
  try {
    const result = await callable(params);
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'setAiProviderConfig:ok',
      data: { ms: Date.now() - t0, provider: params.provider, openrouterModel: params.openrouterModel },
    });
    return result.data;
  } catch (e) {
    useDebugLogStore.getState().add({
      area: 'settings',
      event: 'setAiProviderConfig:error',
      data: { ms: Date.now() - t0, message: getCallableErrorMessage(e), code: getCallableErrorCode(e) },
    });
    throw e;
  }
}

export async function generateHtmlWithUserKey(prompt: string): Promise<GenerateHtmlResponse> {
  function summarizeHtml(html: string) {
    const classMatches = Array.from(html.matchAll(/\bclass\s*=\s*["']([^"']+)["']/gi));
    let classTokens = 0;
    for (const m of classMatches) {
      const raw = String(m[1] ?? '').trim();
      if (!raw) continue;
      classTokens += raw.split(/\s+/g).filter(Boolean).length;
    }
    const avgTokens = classMatches.length ? Math.round((classTokens / classMatches.length) * 10) / 10 : 0;
    return {
      htmlChars: html.length,
      classAttrs: classMatches.length,
      classTokens,
      avgClassTokens: avgTokens,
      hasClassName: /\bclassName\s*=/.test(html),
      hasStyleTag: /<style[\s>]/i.test(html),
      tailwindish: /\b(min-h-screen|bg-|text-|rounded|shadow|space-y-|gap-|grid|flex)\b/.test(html),
    };
  }

  // Single API call without retries - 2 min timeout is sufficient.
  const callable = httpsCallable<{ prompt: string }, GenerateHtmlResponse>(functions, 'generateHtmlWithUserKey', {
    timeout: 300000, // 5 min (function timeout is 300s; keep in sync)
  });
  const t0 = Date.now();
  const preview = prompt.slice(0, 240);
  try {
    const result = await callable({ prompt });
    useDebugLogStore.getState().add({
      area: 'ai',
      event: 'generateHtmlWithUserKey:ok',
      data: {
        ms: Date.now() - t0,
        promptChars: prompt.length,
        promptPreview: preview,
        usage: result.data.usage,
        html: summarizeHtml(result.data.html),
      },
    });
    return result.data;
  } catch (e) {
    useDebugLogStore.getState().add({
      area: 'ai',
      event: 'generateHtmlWithUserKey:error',
      data: {
        ms: Date.now() - t0,
        promptChars: prompt.length,
        promptPreview: preview,
        message: getCallableErrorMessage(e),
        code: getCallableErrorCode(e),
      },
    });
    throw e;
  }
}

export async function generateElementWithGlm(prompt: string): Promise<GenerateHtmlResponse> {
  const callable = httpsCallable<{ prompt: string }, GenerateHtmlResponse>(functions, 'generateElementWithGlm', {
    timeout: 180000,
  });
  const t0 = Date.now();
  const preview = prompt.slice(0, 240);
  console.info('[AI][GLM] request:start', {
    callable: 'generateElementWithGlm',
    promptChars: prompt.length,
    promptPreview: preview,
  });
  try {
    const result = await callable({ prompt });
    console.info('[AI][GLM] request:ok', {
      callable: 'generateElementWithGlm',
      ms: Date.now() - t0,
      usage: result.data.usage,
      htmlChars: result.data.html.length,
    });
    useDebugLogStore.getState().add({
      area: 'ai',
      event: 'generateElementWithGlm:ok',
      data: {
        ms: Date.now() - t0,
        promptChars: prompt.length,
        promptPreview: preview,
        usage: result.data.usage,
      },
    });
    return result.data;
  } catch (e) {
    console.info('[AI][GLM] request:error', {
      callable: 'generateElementWithGlm',
      ms: Date.now() - t0,
      message: getCallableErrorMessage(e),
      code: getCallableErrorCode(e),
    });
    useDebugLogStore.getState().add({
      area: 'ai',
      event: 'generateElementWithGlm:error',
      data: {
        ms: Date.now() - t0,
        promptChars: prompt.length,
        promptPreview: preview,
        message: getCallableErrorMessage(e),
        code: getCallableErrorCode(e),
      },
    });
    throw e;
  }
}
